import { Badge, Box, Button, Group, Menu, ScrollArea, Text, TextInput } from "@mantine/core";
import { IconCheck, IconChevronDown, IconCircleCheck, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { IActiveIssueFilter } from "./issueFilterSelectors";

interface FilterDropdownProps {
  filter: IActiveIssueFilter;
  value: string[];
  onChange?: (filterKey: string, values: string[]) => void;
  enableSearch?: boolean;
}

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filter,
  value = [],
  onChange,
  enableSearch = false,
}) => {
  const [searchValue, setSearchValue] = useState<string>("");

  const handleToggle = (optionValue: string) => {
    const newValues = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue];
    if (onChange) {
      onChange(filter.filterKey, newValues);
    }
  };

  const handleClearAll = () => {
    if (onChange) {
      onChange(filter.filterKey, []);
    }
  };

  const handleSelectAll = () => {
    if (onChange) {
      onChange(
        filter.filterKey,
        filter.options.map((opt) => opt.value),
      );
    }
  };

  const numberOfActiveFilters = value.length;
  const variant = "outline";
  const buttonColor = numberOfActiveFilters > 0 ? "mc-primary" : "warmgray.6";
  const filteredOptions = filter.options.filter((item) =>
    !enableSearch || searchValue === "" ? true : item.label?.toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <Box style={{ whiteSpace: "nowrap" }}>
      <Menu width={220} shadow="sm" position="bottom-start" withinPortal closeOnItemClick={false}>
        <Menu.Target>
          <Button
            px="xs"
            variant={variant}
            color={buttonColor}
            bg={numberOfActiveFilters > 0 ? "#e8eef4" : "white"}
            size="sm"
            rightSection={<IconChevronDown size={16} />}
            style={{ display: "flex", alignItems: "center" }}
          >
            <Group gap={6} align="center" wrap="nowrap">
              <Text>{filter.label}</Text>
              {numberOfActiveFilters > 0 && (
                <Badge size="xs" fz={"xs"} circle>
                  {numberOfActiveFilters}
                </Badge>
              )}
            </Group>
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {enableSearch && (
            <TextInput
              autoFocus
              placeholder="Søk"
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              size="sm"
            />
          )}
          {enableSearch && <Menu.Divider />}
          <ScrollArea style={{ maxHeight: "200px", overflowY: "auto" }} type="auto">
            {filteredOptions.map((option) => (
              <Menu.Item
                key={option.value}
                style={{ fontSize: "0.9em" }}
                onClick={() => handleToggle(option.value)}
                color={value.includes(option.value) ? "mc-primary" : undefined}
                rightSection={value.includes(option.value) ? <IconCheck size={14} /> : null}
              >
                {option.label}
              </Menu.Item>
            ))}
          </ScrollArea>
          {(typeof handleClearAll === "function" || typeof handleSelectAll === "function") && <Menu.Divider />}
          {typeof handleClearAll === "function" && (
            <Menu.Item style={{ fontSize: "0.9em" }} onClick={handleClearAll} leftSection={<IconX size={14} />}>
              Avmarkerer alle
            </Menu.Item>
          )}
          {typeof handleSelectAll === "function" && (
            <Menu.Item
              style={{ fontSize: "0.9em" }}
              onClick={handleSelectAll}
              leftSection={<IconCircleCheck size={14} />}
            >
              Marker alle
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Box>
  );
};
